import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { lastValueFrom } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { EntityDto, PermissionDto, UserDto } from '@dto';
import { toCamelCase, toSnakeCase } from '@helper';
import { AccountService, DeveloperService, EntityService, ResourceService, UserService } from '@service';

@Component({
  selector: 'login-permissions',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrl: './login-permissions.component.scss',
  templateUrl: './login-permissions.component.html',
})
export class LoginPermissionsComponent {
  selectedUser: UserDto;
  resourcesFormControl = new FormControl<string[]>([]);
  resourceEntityMap = new Map<string, { entity: EntityDto; form: FormGroup }>();
  externalSourceList = ['APPLICATIONS', 'TOPOLOGY', 'IDENTITY_USER'];
  showUserSpinner = false;

  constructor(
    public userService: UserService,
    public resourceService: ResourceService,
    public developerService: DeveloperService,
    private accountService: AccountService,
    public entityService: EntityService,
    private fb: FormBuilder,
  ) {}

  async changeUser(user: UserDto): Promise<void> {
    this.showUserSpinner = true;
    this.selectedUser = user;
    await lastValueFrom(this.userService.userGroupedPermissions$(user.id));
    const permissions = this.userService.groupedPermissions().permissions;
    const permissionsId = Object.keys(permissions);
    const uppered = permissionsId.map(key => toSnakeCase(key).toUpperCase());
    this.resourcesFormControl.setValue(uppered);
    this.showUserSpinner = false;
  }

  async externalSourceChange(source: string, resource: string, extId?: string): Promise<void> {
    let entity: EntityDto;
    if (source === 'APPLICATIONS') {
      entity = this.entityService.entites.find(entity => entity.extSource === 'APPLICATIONS');
      this.resourceEntityMap.set(resource, { entity, form: undefined });
      this.initForm(resource, entity);
    } else if (extId) {
      entity = this.entityService.entites.find(entity => entity.extSource === source && entity.extId === extId);
      if (!entity) {
        entity = await lastValueFrom(this.entityService.createEntity$({ extId: extId, extSource: source }));
      }
    }
    if (!entity) {
      entity = {
        extSource: source,
        ...entity,
      };
    }
    this.resourceEntityMap.set(resource, { entity, form: undefined });
    if (entity.id) {
      this.initForm(resource, entity);
    }
  }

  togglePermission(
    event: MatCheckboxChange,
    resourceId: string,
    entityId: string,
    checkbox: MatCheckbox,
    resourceContainer: HTMLDivElement,
  ): void {
    const spinner = resourceContainer.querySelector('mat-spinner') as HTMLElement;
    spinner.style.visibility = 'visible';
    const permission: PermissionDto = {
      entityId,
      operations: [event.source.value],
      resourceId,
    };
    if (event.checked) {
      this.userService
        .addUserPermission$(this.selectedUser.id, permission)
        .pipe(
          catchError(error => {
            checkbox.checked = false;
            spinner.style.visibility = 'hidden';
            throw new Error(error);
          }),
          switchMap(() => this.userService.userGroupedPermissions$(this.selectedUser.id)),
        )
        .subscribe(() => (spinner.style.visibility = 'hidden'));
    } else {
      this.userService
        .deleteUserPermission$(this.selectedUser.id, permission)
        .pipe(
          catchError(error => {
            checkbox.checked = true;
            spinner.style.visibility = 'hidden';
            throw new Error(error);
          }),
          switchMap(() => this.userService.userGroupedPermissions$(this.selectedUser.id)),
        )
        .subscribe(() => (spinner.style.visibility = 'hidden'));
    }
  }

  deleteEntityFromResource(
    resourceId: string,
    entityId: string,
    spinner: MatProgressSpinner,
    matSelect: MatSelect,
  ): void {
    spinner._elementRef.nativeElement.style.visibility = 'visible';
    const operations = this.userService.groupedPermissions().permissions[toCamelCase(resourceId)][entityId];
    const permission: PermissionDto = {
      entityId,
      operations,
      resourceId,
    };
    this.userService
      .deleteUserPermission$(this.selectedUser.id, permission)
      .pipe(switchMap(() => this.userService.userGroupedPermissions$(this.selectedUser.id)))
      .subscribe({
        complete: () => (spinner._elementRef.nativeElement.style.visibility = 'hidden'),
        error: err => console.error('Observable emitted an error: ' + err),
        next: () => matSelect.writeValue(null),
      });
  }

  getCrudeForm(resourceId: string): FormGroup {
    return this.resourceEntityMap?.get(resourceId)?.form;
  }

  getEntityLabel(id: string): string {
    const entity = this.entityService.entites.find(entity => entity.id === id);
    switch (entity.extSource) {
      case 'APPLICATIONS':
        return 'APPLICATIONS';
      case 'TOPOLOGY': {
        const developer = this.developerService.developers.find(developer => developer.id === entity.extId);
        const account = this.accountService.accounts.find(account => account.id === entity.extId);
        return `TOPOLOGY | ${developer?.name ?? account?.displayLabel ?? id}`;
      }
      case 'IDENTITY_USER': {
        const user = this.userService.users().find(user => user.id === entity.extId);
        return `IDENTITY_USER | ${user?.username}`;
      }
      default:
        return id;
    }
  }

  private initForm(resource: string, entity: EntityDto): void {
    const form = this.fb.group({
      C: this.hasPermission('C', resource, entity.id),
      D: this.hasPermission('D', resource, entity.id),
      E: this.hasPermission('E', resource, entity.id),
      R: this.hasPermission('R', resource, entity.id),
      U: this.hasPermission('U', resource, entity.id),
    });
    this.resourceEntityMap.set(resource, { entity, form });
  }

  private hasPermission(permission: string, resource: string, entityId: string): boolean {
    if (
      !this.userService.groupedPermissions()?.permissions ||
      !Object.keys(this.userService.groupedPermissions().permissions)?.length
    ) {
      return false;
    }
    if (!this.userService.groupedPermissions().permissions[toCamelCase(resource)]) {
      return false;
    }
    return this.userService.groupedPermissions().permissions[toCamelCase(resource)][entityId]?.includes(permission);
  }

  protected readonly toCamelCase = toCamelCase;
}
